// @flow

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import _ from 'lodash/fp';
import AdminList from '../../components/AdminList/AdminList';
import SelectFormInput from '../../components/SelectFormInput/SelectFormInput';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import SearchField from '../../components/SearchField/SearchField';
import {
  fetchOrders,
  filterOrders,
  sortItems,
} from '../../actions/orderList';
import {states} from '../../orderStates';
import booleanValues from '../../booleanValues';
import {formatOrderState} from '../../helpers/tableHelpers';

const fixedStates = _.map(s => ({
  ...s,
  id: s.id + 1,
}), states);

const columns = [{
  property: 'apartment.project.name',
  header: {
    label: 'Projekt',
  },
}, {
  property: 'apartment.objectId',
  header: {
    label: 'Lägenhet',
  },
}, {
  property: 'user.person.name',
  header: {
    label: 'Kund',
  },
}, {
  property: 'totalPrice',
  header: {
    label: 'Värde',
  },
}, {
  property: 'state',
  header: {
    label: 'Status',
  },
  cell: {
    formatters: [
      formatOrderState,
    ],
  },
}];

export type Props = {
  history: Object,
  salesUsers: any[],
  error: ?any,
  fetchOrders: Function,
  filter: {
    archived: boolean,
    orderState: ?number,
    salesUserId: ?number,
    projectId: ?number,
    query: ?string,
  },
  filterOrders: Function,
  isFetching: boolean,
  orders: any[],
  location: { pathname: string },
  projects: any[],
  sortItems: typeof sortItems,
  readonly: boolean,
};

class OrdersContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchOrders();
  }

  filterByQuery = e => {
    const {
      filter,
      filterOrders,
    } = this.props;

    filterOrders({
      ...filter,
      query: e.target.value,
    });
  }

  clearQuery = () => {
    this.filterByQuery({target: {value: ''}});
  }

  filterByProjectId = project => {
    const {
      filter,
      filterOrders,
    } = this.props;

    filterOrders({
      ...filter,
      projectId: project ? project.id : null,
    });
  }

  filterBySalesUserId = user => {
    const {
      filter,
      filterOrders,
    } = this.props;

    filterOrders({
      ...filter,
      salesUserId: user ? user.id : null,
    });
  }

  filterByOrderState = state => {
    const {
      filter,
      filterOrders,
    } = this.props;

    filterOrders({
      ...filter,
      orderState: state ? state.id : null,
    });
  }

  filterByArchived = archived => {
    const {
      filter,
      filterOrders,
    } = this.props;

    filterOrders({
      ...filter,
      archived: archived ? archived.value : false,
    });
  }

  render() {
    const {
      history,
      error,
      filter,
      isFetching,
      orders,
      salesUsers,
      location: {pathname},
      projects,
      sortItems,
      readonly,
    } = this.props;
    return (
      <div className="OrdersContainer relative">
        <ServerError error={error} />
        <LoadingIndicator isLoading={isFetching} />
        <div className="row align-middle">
          <div className="column">
            <SearchField
              value={filter.query || ''}
              onClear={this.clearQuery}
              onChange={this.filterByQuery} />
          </div>
          {readonly ? null :
            <div className="column shrink">
              <button
                type="button"
                className="primary button"
                onClick={() => history.push(`${pathname}/new`)}>
              Skapa ny
              </button>
            </div>
          }
        </div>
        <div className="box">
          <div className="row align-middle">
            {projects &&
              <div className="column">
                <SelectFormInput
                  input={{
                    value: filter.projectId || null,
                    onBlur: _.noop,
                    onChange: this.filterByProjectId,
                  }}
                  label="Projekt"
                  meta={{}}
                  valueKey="id"
                  labelKey="name"
                  clearable={true}
                  options={projects}
                  placeholder="Alla"
                  isLoading={isFetching} />
              </div>
            }
            <div className="column">
              <SelectFormInput
                input={{
                  value: filter.salesUserId,
                  onBlur: _.noop,
                  onChange: this.filterBySalesUserId,
                }}
                label="Säljare"
                meta={{}}
                valueKey="id"
                labelKey="username"
                clearable={true}
                options={salesUsers}
                placeholder="Alla"
                isLoading={isFetching} />
            </div>
            <div className="column">
              <SelectFormInput
                input={{
                  value: filter.orderState,
                  onBlur: _.noop,
                  onChange: this.filterByOrderState,
                }}
                label="Status"
                meta={{}}
                valueKey="id"
                labelKey="label"
                clearable={true}
                options={fixedStates}
                placeholder="Alla" />
            </div>
            <div className="column">
              <SelectFormInput
                input={{
                  value: !!filter.archived,
                  onBlur: _.noop,
                  onChange: this.filterByArchived,
                }}
                label="Arkiverade"
                meta={{}}
                valueKey="value"
                labelKey="label"
                clearable={false}
                options={booleanValues} />
            </div>
          </div>
          {orders && (
            filter.orderState !== null
            || filter.salesUserId !== null
            || filter.projectId !== null
            || (filter.query !== null && filter.query !== '')
          ) ? (
              <AdminList
                query={filter.query}
                columns={columns}
                rows={orders}
                onSort={sortItems}
                onRowSelected={row => readonly ? null : history.push(`${pathname}/${row.id}`) }
                sortColumnIndex="1" />
            )
            : (
              <div className="callout secondary">
                Gör ett val eller en sökning för att se resultat.
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({
  orderList: {
    salesUsers,
    error,
    filter,
    isFetching,
    filteredOrders: orders,
    projects,
  },
}) => ({
  salesUsers,
  error,
  filter,
  isFetching,
  orders,
  projects,
});

const mapDispatchToProps = dispatch => ({
  fetchOrders: () =>
    dispatch(fetchOrders()),
  filterOrders: filter =>
    dispatch(filterOrders(filter)),
  sortItems: sorter =>
    dispatch(sortItems(sorter)),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(OrdersContainer));



// WEBPACK FOOTER //
// ./src/containers/OrdersContainer/OrdersContainer.js